import React, { useState, useEffect } from 'react';
import { X, Shield, AlertTriangle, Code, BookOpen, Target, Zap, Brain, Copy, Check, ExternalLink, Github, Loader2, Sparkles, Link2, Terminal, Database, FileCode, Bug, Crosshair, TrendingUp, AlertOctagon, Clock } from 'lucide-react';
import MarkdownRenderer from './common/MarkdownRenderer';
import { useI18n } from '../i18n';

const CVEDetailPanel = ({ cve, isOpen, onClose }) => {
  const { t } = useI18n();
  const [llmExplanation, setLlmExplanation] = useState(null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exploitGuide, setExploitGuide] = useState(null);
  const [loadingExploitGuide, setLoadingExploitGuide] = useState(false);
  const [showAdvancedGuide, setShowAdvancedGuide] = useState(false);
  // SploitScan intelligence
  const [sploitScanData, setSploitScanData] = useState(null);
  const [loadingSploitScan, setLoadingSploitScan] = useState(false);

  useEffect(() => {
    if (isOpen && cve) {
      fetchLLMExplanation();
      fetchSploitScanIntel();
      setExploitGuide(null);
      setShowAdvancedGuide(false);
    }
  }, [isOpen, cve]);

  // Fetch SploitScan intelligence (EPSS, KEV, exploits)
  const fetchSploitScanIntel = async () => {
    if (!cve?.cve_id) return;
    setLoadingSploitScan(true);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${backendUrl}/api/v1/sploitscan/quick-lookup/${cve.cve_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSploitScanData(data);
      }
    } catch (error) {
      console.error('Failed to fetch SploitScan intel:', error);
    } finally {
      setLoadingSploitScan(false);
    }
  };

  const fetchLLMExplanation = async () => {
    setLoadingExplanation(true);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/v1/ai/explain-cve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          cve_id: cve.cve_id,
          title: cve.title,
          description: cve.description,
          severity: cve.severity
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLlmExplanation(data);
      }
    } catch (error) {
      console.error('Failed to fetch LLM explanation:', error);
      // Fallback to generated explanation
      setLlmExplanation({
        risk_explanation: `The vulnerability ${cve.cve_id} presents a ${cve.severity.toLowerCase()} risk to your system. It affects ${cve.affected_component || 'the targeted component'} and could allow an attacker to compromise security.`,
        exploitation_guide: "1. Identify the vulnerable version\n2. Search for public exploits\n3. Adapt the exploit to context\n4. Test in a controlled environment\n5. Document results",
        mitigation: "Apply the security patch as soon as possible. In the meantime, isolate the affected component and monitor for exploitation attempts.",
        references: ["https://nvd.nist.gov/vuln/detail/" + cve.cve_id]
      });
    } finally {
      setLoadingExplanation(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Fetch advanced exploitation guide from SYLink LLM
  const fetchExploitGuide = async () => {
    if (exploitGuide) return; // Already loaded
    setLoadingExploitGuide(true);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${backendUrl}/api/v1/ai/exploit-guide`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          cve_id: cve.cve_id,
          title: cve.title,
          description: cve.description,
          severity: cve.severity,
          affected_component: cve.affected_component,
          port: cve.port,
          service: cve.service
        })
      });

      if (response.ok) {
        const data = await response.json();
        setExploitGuide(data);
      } else {
        // Fallback guide
        setExploitGuide({
          steps: [
            { step: 1, title: "Reconnaissance", description: "Identify the exact version of the vulnerable component", command: `nmap -sV -p${cve.port || '80'} [TARGET]` },
            { step: 2, title: "Recherche d'exploits", description: "Rechercher des PoC publics sur GitHub et Exploit-DB", command: `searchsploit ${cve.cve_id}` },
            { step: 3, title: "Preparation", description: "Adapt the exploit to the target context", command: null },
            { step: 4, title: "Exploitation", description: "Execute the exploit in a controlled environment", command: null },
            { step: 5, title: "Post-exploitation", description: "Document evidence and assess impact", command: null }
          ],
          resources: [
            { name: "NVD", url: `https://nvd.nist.gov/vuln/detail/${cve.cve_id}`, type: "official" },
            { name: "Exploit-DB", url: `https://www.exploit-db.com/search?cve=${cve.cve_id?.replace('CVE-', '')}`, type: "exploits" },
            { name: "GitHub PoC", url: `https://github.com/search?q=${cve.cve_id}+exploit&type=repositories`, type: "github" },
            { name: "MITRE", url: `https://cve.mitre.org/cgi-bin/cvename.cgi?name=${cve.cve_id}`, type: "official" }
          ],
          tools: ["nmap", "metasploit", "searchsploit", "nuclei"],
          risk_warning: "This exploitation must only be performed within a legally authorized context (pentest, CTF, test environment)."
        });
      }
    } catch (error) {
      console.error('Failed to fetch exploit guide:', error);
      setExploitGuide(null);
    } finally {
      setLoadingExploitGuide(false);
    }
  };

  if (!isOpen || !cve) return null;

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'from-red-900/40 to-red-800/20 border-red-500',
      'high': 'from-orange-900/40 to-orange-800/20 border-orange-500',
      'medium': 'from-yellow-900/40 to-yellow-800/20 border-yellow-500',
      'low': 'from-blue-900/40 to-blue-800/20 border-blue-500'
    };
    return colors[severity?.toLowerCase()] || colors.medium;
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-end">
      {/* Overlay - Click to close */}
      <div className="absolute inset-0" onClick={onClose}></div>
      
      {/* Slide Panel */}
      <div className="relative bg-brand-black-light h-full w-full max-w-3xl border-l-2 border-brand-green/30 overflow-y-auto animate-slide-in-right">
        {/* Header */}
        <div className={`sticky top-0 bg-gradient-to-r ${getSeverityColor(cve.severity)} border-b-2 p-6 z-10`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-8 h-8 text-red-400" />
                <div>
                  <h2 className="text-2xl font-bold text-white">{cve.cve_id}</h2>
                  <p className="text-gray-300">{cve.title}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${
                  cve.severity === 'critical' ? 'bg-red-500/20 text-red-400 border-red-500' :
                  cve.severity === 'high' ? 'bg-orange-500/20 text-orange-400 border-orange-500' :
                  cve.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500' :
                  'bg-blue-500/20 text-blue-400 border-blue-500'
                }`}>
                  {t(`vulnerabilities.severities.${cve.severity}`)}
                </span>
                {cve.cvss_score && (
                  <span className="px-3 py-1 bg-brand-black/50 text-white rounded-lg border border-gray-600">
                    CVSS: {cve.cvss_score}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-black rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400 hover:text-brand-green" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div className="bg-brand-black rounded-xl border border-gray-700 p-6">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-brand-green" />
              <h3 className="text-lg font-bold text-white">Description</h3>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">{cve.description}</p>
          </div>

          {/* References - MOVED HERE right after Description */}
          {llmExplanation?.references && llmExplanation.references.length > 0 && (
            <div className="bg-brand-black rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-5 h-5 text-brand-green" />
                <h3 className="text-lg font-bold text-white">References</h3>
              </div>
              <ul className="space-y-2">
                {llmExplanation.references.map((ref, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <ExternalLink className="w-3 h-3 text-gray-500 flex-shrink-0" />
                    <a
                      href={ref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-green hover:text-brand-green-light text-sm underline break-all"
                    >
                      {ref}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Technical Details */}
          <div className="bg-brand-black rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Technical Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {cve.affected_component && (
                <div>
                  <p className="text-gray-500 mb-1">Affected Component</p>
                  <p className="text-white font-medium">{cve.affected_component}</p>
                </div>
              )}
              {cve.port && (
                <div>
                  <p className="text-gray-500 mb-1">Port</p>
                  <p className="text-white font-medium">{cve.port}</p>
                </div>
              )}
              {cve.service && (
                <div>
                  <p className="text-gray-500 mb-1">Service</p>
                  <p className="text-white font-medium">{cve.service}</p>
                </div>
              )}
              {cve.discovered_at && (
                <div>
                  <p className="text-gray-500 mb-1">Discovered on</p>
                  <p className="text-white font-medium">{new Date(cve.discovered_at).toLocaleString('fr-FR')}</p>
                </div>
              )}
            </div>
          </div>

          {/* SploitScan Intelligence */}
          {loadingSploitScan ? (
            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl border border-purple-500/30 p-6 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-purple-400 animate-spin mr-3" />
              <span className="text-gray-400">Chargement intelligence SploitScan...</span>
            </div>
          ) : sploitScanData && sploitScanData.sploitscan_available && (
            <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-xl border border-purple-500/30 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Bug className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-bold text-purple-400">SploitScan Intelligence</h3>
                {sploitScanData.patch_priority && (
                  <span className={`ml-auto px-3 py-1 rounded-lg font-bold text-sm border ${
                    sploitScanData.patch_priority === 'A+' ? 'bg-red-500/20 text-red-400 border-red-500' :
                    sploitScanData.patch_priority === 'A' ? 'bg-orange-500/20 text-orange-400 border-orange-500' :
                    sploitScanData.patch_priority === 'B' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500' :
                    'bg-gray-500/20 text-gray-400 border-gray-500'
                  }`}>
                    Priority {sploitScanData.patch_priority}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* EPSS Score */}
                <div className="bg-brand-black/50 rounded-lg p-3 border border-gray-700">
                  <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                    <TrendingUp className="w-3 h-3" />
                    EPSS Score
                  </div>
                  <p className={`text-xl font-bold ${
                    sploitScanData.epss_score >= 0.7 ? 'text-red-400' :
                    sploitScanData.epss_score >= 0.4 ? 'text-orange-400' :
                    sploitScanData.epss_score >= 0.1 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {sploitScanData.epss_score !== null
                      ? `${(sploitScanData.epss_score * 100).toFixed(1)}%`
                      : 'N/A'}
                  </p>
                </div>

                {/* KEV Status */}
                <div className={`rounded-lg p-3 border ${
                  sploitScanData.in_kev
                    ? 'bg-red-500/10 border-red-500/50'
                    : 'bg-brand-black/50 border-gray-700'
                }`}>
                  <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                    <AlertOctagon className="w-3 h-3" />
                    CISA KEV
                  </div>
                  {sploitScanData.in_kev ? (
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400 font-bold">EXPLOITED</span>
                    </div>
                  ) : (
                    <p className="text-green-400 font-bold flex items-center gap-1">
                      <Check className="w-4 h-4" /> Non
                    </p>
                  )}
                </div>

                {/* Exploit Count */}
                <div className={`rounded-lg p-3 border ${
                  sploitScanData.exploit_count > 0
                    ? 'bg-orange-500/10 border-orange-500/50'
                    : 'bg-brand-black/50 border-gray-700'
                }`}>
                  <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                    <Zap className="w-3 h-3" />
                    Exploits
                  </div>
                  <p className={`text-xl font-bold ${
                    sploitScanData.exploit_count > 0 ? 'text-orange-400' : 'text-gray-400'
                  }`}>
                    {sploitScanData.exploit_count || 0}
                  </p>
                </div>

                {/* Metasploit */}
                <div className={`rounded-lg p-3 border ${
                  sploitScanData.has_metasploit
                    ? 'bg-red-500/10 border-red-500/50'
                    : 'bg-brand-black/50 border-gray-700'
                }`}>
                  <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
                    <Terminal className="w-3 h-3" />
                    Metasploit
                  </div>
                  {sploitScanData.has_metasploit ? (
                    <p className="text-red-400 font-bold flex items-center gap-1">
                      <Check className="w-4 h-4" /> Disponible
                    </p>
                  ) : (
                    <p className="text-gray-400">Non</p>
                  )}
                </div>
              </div>

              {/* Warning if in KEV */}
              {sploitScanData.in_kev && (
                <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-semibold">Active Exploitation Confirmed</p>
                    <p className="text-red-300 text-sm">This CVE is in the CISA KEV catalog - Immediate patch required!</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LLM Risk Explanation */}
          {loadingExplanation ? (
            <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6 flex items-center justify-center">
              <div className="animate-spin w-6 h-6 border-2 border-brand-green border-t-transparent rounded-full mr-3"></div>
              <span className="text-gray-400">AI analyzing the vulnerability...</span>
            </div>
          ) : llmExplanation && (
            <>
              {/* Risk Explanation */}
              <div className="bg-gradient-to-br from-brand-green/10 to-transparent rounded-xl border border-brand-green/30 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-brand-green" />
                  <h3 className="text-lg font-bold text-brand-green">Explication du Risque (IA)</h3>
                </div>
                <MarkdownRenderer content={llmExplanation.risk_explanation} variant="compact" />
              </div>

              {/* Basic Exploitation Guide */}
              <div className="bg-gradient-to-br from-orange-900/20 to-transparent rounded-xl border border-orange-500/30 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-orange-400" />
                  <h3 className="text-lg font-bold text-orange-400">Comment Exploiter (Guide LLM)</h3>
                </div>
                <div className="bg-brand-black/50 rounded-lg p-4 border border-orange-500/20">
                  <MarkdownRenderer content={llmExplanation.exploitation_guide} variant="compact" />
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => copyToClipboard(llmExplanation.exploitation_guide)}
                    className="flex items-center gap-2 px-3 py-2 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 transition-colors text-sm"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy guide'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAdvancedGuide(true);
                      fetchExploitGuide();
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm border border-red-500/30"
                  >
                    <Crosshair className="w-4 h-4" />
                    Advanced Guide + Sources
                  </button>
                </div>
              </div>

              {/* ADVANCED EXPLOITATION GUIDE - New Section */}
              {showAdvancedGuide && (
                <div className="bg-gradient-to-br from-red-900/30 to-purple-900/20 rounded-xl border-2 border-red-500/50 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-6 h-6 text-red-400 animate-pulse" />
                    <h3 className="text-xl font-bold text-red-400">Advanced Exploitation Guide (SYLink AI)</h3>
                  </div>

                  {loadingExploitGuide ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 text-red-400 animate-spin mr-3" />
                      <span className="text-gray-400">Generating advanced guide...</span>
                    </div>
                  ) : exploitGuide && (
                    <div className="space-y-5">
                      {/* Warning Banner */}
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <p className="text-yellow-400 text-xs">{exploitGuide.risk_warning}</p>
                      </div>

                      {/* Step-by-Step Guide */}
                      <div>
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <Bug className="w-4 h-4 text-red-400" />
                          Exploitation Steps
                        </h4>
                        <div className="space-y-3">
                          {exploitGuide.steps?.map((step, idx) => (
                            <div key={idx} className="bg-brand-black/60 rounded-lg p-4 border border-gray-700">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="w-7 h-7 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-sm font-bold border border-red-500/40">
                                  {step.step}
                                </span>
                                <span className="text-white font-medium">{step.title}</span>
                              </div>
                              <p className="text-gray-400 text-sm ml-10 mb-2">{step.description}</p>
                              {step.command && (
                                <div className="ml-10 bg-brand-black rounded-lg p-2 border border-gray-600 flex items-center justify-between">
                                  <code className="text-brand-green text-xs font-mono">{step.command}</code>
                                  <button
                                    onClick={() => copyToClipboard(step.command)}
                                    className="text-gray-500 hover:text-brand-green transition-colors"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Resources & Links */}
                      <div>
                        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                          <Database className="w-4 h-4 text-purple-400" />
                          Ressources & Exploits
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          {exploitGuide.resources?.map((resource, idx) => (
                            <a
                              key={idx}
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-all hover:scale-[1.02] ${
                                resource.type === 'github'
                                  ? 'bg-gray-800/50 border-gray-600 hover:border-white'
                                  : resource.type === 'exploits'
                                  ? 'bg-red-900/30 border-red-500/40 hover:border-red-400'
                                  : 'bg-blue-900/30 border-blue-500/40 hover:border-blue-400'
                              }`}
                            >
                              {resource.type === 'github' ? (
                                <Github className="w-5 h-5 text-white" />
                              ) : resource.type === 'exploits' ? (
                                <FileCode className="w-5 h-5 text-red-400" />
                              ) : (
                                <Shield className="w-5 h-5 text-blue-400" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${
                                  resource.type === 'github' ? 'text-white' :
                                  resource.type === 'exploits' ? 'text-red-400' : 'text-blue-400'
                                }`}>{resource.name}</p>
                                <p className="text-xs text-gray-500 truncate">{resource.url}</p>
                              </div>
                              <ExternalLink className="w-4 h-4 text-gray-500" />
                            </a>
                          ))}
                        </div>
                      </div>

                      {/* Recommended Tools */}
                      {exploitGuide.tools && exploitGuide.tools.length > 0 && (
                        <div>
                          <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                            <Terminal className="w-4 h-4 text-brand-green" />
                            Recommended Tools
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {exploitGuide.tools.map((tool, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1.5 bg-brand-green/10 text-brand-green rounded-lg text-sm font-mono border border-brand-green/30"
                              >
                                {tool}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Mitigation */}
              <div className="bg-gradient-to-br from-blue-900/20 to-transparent rounded-xl border border-blue-500/30 p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-blue-400" />
                  <h3 className="text-lg font-bold text-blue-400">Recommended Remediation</h3>
                </div>
                <MarkdownRenderer content={llmExplanation.mitigation} variant="compact" />
              </div>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CVEDetailPanel;
